import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card } from "../../../components/ui/card";
import SearchBar from "../../../components/ui/SearchBar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faShoppingCart,
  faUserTie,
  faBoxes,
  faBox,
  faPlus,
  faTimes,
  faTrash,
  faExclamationTriangle,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import { useAuth } from "../../../hooks";

const AddSale = ({ onAdd, onClose, sellers = [], stockData = [] }) => {
  const { user } = useAuth();
  const isSeller = user?.role === "seller";

  const [form, setForm] = useState({
    sellerId: "",
    productTypeId: "",
    productCategoryId: "",
  });
  const [productQuantities, setProductQuantities] = useState({});
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [error, setError] = useState("");
  const [tableError, setTableError] = useState("");
  const [errorProductId, setErrorProductId] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [customSellingPrices, setCustomSellingPrices] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedProductKey, setExpandedProductKey] = useState(null);

  const activeSellers = useMemo(() => {
    return (Array.isArray(sellers) ? sellers : [])
      .filter((seller) => seller?.status?.toLowerCase() === "active")
      .map((seller) => {
        const sellerId = String(
          seller?.shop_saller_id || seller?.shopSellerId || seller?.id || "",
        );
        return {
          ...seller,
          _sellerId: sellerId,
          _sellerUsername: seller?.username || "",
          _sellerName:
            seller?.user?.full_name || seller?.username || "Unknown Seller",
          _sellerRegNo: seller?.username || "N/A",
          _sellerEmail: seller?.user?.email || "N/A",
          _sellerPhone: seller?.user?.phone || "N/A",
        };
      });
  }, [sellers]);

  const autoSeller = useMemo(() => {
    if (!isSeller) return null;
    return (
      activeSellers.find((s) => s._sellerUsername === user?.username) || null
    );
  }, [isSeller, activeSellers, user?.username]);

  useEffect(() => {
    if (isSeller && autoSeller) {
      setForm((prev) => ({ ...prev, sellerId: autoSeller._sellerId }));
    }
  }, [isSeller, autoSeller]);

  const selectedSeller = useMemo(
    () =>
      activeSellers.find((seller) => seller._sellerId === form.sellerId) ||
      null,
    [activeSellers, form.sellerId],
  );

  const activeStockData = useMemo(() => {
    return (Array.isArray(stockData) ? stockData : []).filter((stock) => {
      const isActive = stock?.status?.toLowerCase() === "active";
      const remainingItems = Number(
        stock?.summary?.remainingItems ??
          stock?.remainingItems ??
          stock?.remaining_items ??
          0,
      );
      const totalItems = Number(
        stock?.summary?.totalItems ??
          stock?.totalItems ??
          stock?.total_items ??
          0,
      );
      const hasRemaining = (remainingItems || totalItems) > 0;
      return isActive && hasRemaining;
    });
  }, [stockData]);

  const enrichedStockData = useMemo(() => {
    return activeStockData.map((stock) => {
      const product = stock.product || stock;
      const category = product.product_category ||
        product.category || {
          category_name:
            product.productCategory || product.product_category_name,
          product_type:
            product.product_type ||
            product.productType ||
            product.product_type_name,
        };
      const type =
        category.product_type ||
        product.product_type ||
        product.productType ||
        {};

      const productTypeName =
        typeof type === "string" ? type : type.name || type.product_type_name;
      const productCategoryName =
        category.category_name ||
        product.productCategory ||
        product.category_name;

      const productTypeId =
        type.productTypeId ||
        type.product_type_id ||
        product.product_type_id ||
        productTypeName;

      const productCategoryId =
        category.productCategoryId ||
        category.product_category_id ||
        product.product_category_id ||
        productCategoryName;

      const totalItems = Number(
        stock.summary?.totalItems ??
          product.no_of_items ??
          stock.noOfItems ??
          stock.totalItems ??
          stock.total_items ??
          1,
      );

      const remainingItems = Number(
        stock.summary?.remainingItems ??
          stock.remainingItems ??
          stock.remaining_items ??
          totalItems,
      );

      return {
        ...stock,
        stockId: stock.stockId || stock.id,
        id: stock.stockId || stock.id,
        productName:
          product.product_name || stock.productName || "Unknown product",
        productTypeId: String(productTypeId || ""),
        productTypeName: productTypeName || "Uncategorized",
        productCategoryId: String(productCategoryId || ""),
        productCategoryName: productCategoryName || "Uncategorized",
        packageType: product.package_type || stock.packageType || "N/A",
        noOfItems: totalItems,
        totalItems,
        remainingItems,
        purchasePrice: Number(
          stock.purchase_price ??
            product.purchase_price ??
            stock.purchasePrice ??
            0,
        ),
        sellingPrice: Number(
          stock.selling_price ??
            product.selling_price ??
            stock.sellingPrice ??
            0,
        ),
        purchaseDate:
          stock.purchase_date ||
          product.purchase_date ||
          stock.purchaseDate ||
          null,
        shopId: stock.shopId || stock.shop_id,
        shopName: stock.shop?.shop_name || stock.shopName,
        summary: {
          totalItems,
          remainingItems,
        },
      };
    });
  }, [activeStockData]);

  const availableProductTypes = useMemo(() => {
    const typeIds = new Set();
    const result = [];

    enrichedStockData.forEach((stock) => {
      if (stock.productTypeId && !typeIds.has(stock.productTypeId)) {
        typeIds.add(stock.productTypeId);
        result.push({ id: stock.productTypeId, name: stock.productTypeName });
      }
    });

    return result;
  }, [enrichedStockData]);

  const getCategoriesForType = (typeId) => {
    if (!typeId) return [];
    const categoryIds = new Set();
    const result = [];

    enrichedStockData.forEach((stock) => {
      if (
        stock.productTypeId === typeId &&
        stock.productCategoryId &&
        !categoryIds.has(stock.productCategoryId)
      ) {
        categoryIds.add(stock.productCategoryId);
        result.push({
          id: stock.productCategoryId,
          name: stock.productCategoryName,
        });
      }
    });

    return result;
  };

  const getProductsForCategory = (typeId, categoryId) => {
    if (!typeId || !categoryId) return [];

    return enrichedStockData.filter(
      (stock) =>
        stock.productTypeId === typeId &&
        stock.productCategoryId === categoryId,
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0 TSH";
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const handleTypeChange = (value) => {
    setForm({ ...form, productTypeId: value, productCategoryId: "" });
    const categories = getCategoriesForType(value);
    setFilteredCategories(categories);
    setFilteredProducts([]);
    setProductQuantities({});
    setCustomSellingPrices({});
    setError("");
    setTableError("");
    setErrorProductId(null);
    setProductSearchTerm("");
    setExpandedProductKey(null);
  };

  const handleCategoryChange = (value) => {
    setForm({ ...form, productCategoryId: value });
    const products = getProductsForCategory(form.productTypeId, value);
    setFilteredProducts(products);

    const initialQuantities = {};
    const initialPrices = {};
    products.forEach((p) => {
      if (p?.stockId) {
        initialQuantities[p.stockId] = "";
        initialPrices[p.stockId] = "";
      }
    });
    setProductQuantities(initialQuantities);
    setCustomSellingPrices(initialPrices);
    setError("");
    setTableError("");
    setErrorProductId(null);
    setProductSearchTerm("");
    setExpandedProductKey(null);
  };

  const handleQuantityChange = (stockId, value) => {
    if (!stockId) return;
    setProductQuantities((prev) => ({ ...prev, [stockId]: value }));
    setError("");
    setTableError("");
    setErrorProductId(null);
  };

  const handleSellingPriceChange = (stockId, value) => {
    if (!stockId) return;
    setCustomSellingPrices((prev) => ({ ...prev, [stockId]: value }));
    setError("");
    setTableError("");
    setErrorProductId(null);
  };

  const addSaleItem = (product) => {
    if (!product) return;

    if (!form.sellerId) {
      setError("Please select a seller first before adding products.");
      toastError("Please select a seller first before adding products.");
      return;
    }

    const seller = selectedSeller;
    if (!seller) {
      setError("Selected seller not found. Please select a valid seller.");
      toastError("Selected seller not found. Please select a valid seller.");
      return;
    }

    const quantity = parseInt(productQuantities[product.stockId]);
    const actualSellingPrice = parseFloat(customSellingPrices[product.stockId]);
    const remainingItems = Number(product.remainingItems ?? 0);

    if (isNaN(quantity) || quantity < 0) {
      const msg = `Please enter a valid number of unsold items for ${product.productName}`;
      setError(msg);
      toastError(msg);
      return;
    }

    if (isNaN(actualSellingPrice) || actualSellingPrice <= 0) {
      const msg = `Please enter the actual sale revenue for ${product.productName}`;
      setError(msg);
      toastError(msg);
      return;
    }

    if (remainingItems <= 0) {
      const errorMsg = `No stock remaining for ${product.productName}.`;
      setTableError(errorMsg);
      setErrorProductId(product.stockId);
      setError("");
      toastError(errorMsg);
      return;
    }

    if (quantity > remainingItems) {
      const errorMsg = `Quantity cannot exceed available stock (${remainingItems} items remaining) for ${product.productName}`;
      setTableError(errorMsg);
      setErrorProductId(product.stockId);
      setError("");
      toastError(errorMsg);
      return;
    }

    if (quantity === remainingItems) {
      const errorMsg = `You must sell at least 1 item. Remaining is ${remainingItems}. Enter a number less than ${remainingItems} (or 0 to sell all).`;
      setTableError(errorMsg);
      setErrorProductId(product.stockId);
      setError("");
      toastError(errorMsg);
      return;
    }

    const existingItem = saleItems.find(
      (item) => item?.stockId === product.stockId,
    );
    if (existingItem) {
      const msg = `${product.productName} (this stock) is already added. Please edit it in the list below.`;
      setError(msg);
      toastError(msg);
      return;
    }

    const totalItems = Number(product.totalItems ?? 0);
    const itemsSold = Math.max(totalItems - quantity, 1);

    const newItem = {
      id: Date.now(),
      sellerId: form.sellerId,
      sellerRegNo: seller._sellerRegNo,
      sellerName: seller._sellerName,
      sellerEmail: seller._sellerEmail,
      sellerPhone: seller._sellerPhone,
      stockId: product.stockId,
      productName: product.productName,
      productType: product.productTypeName,
      productCategory: product.productCategoryName,
      packageType: product.packageType || "N/A",
      noOfItems: product.noOfItems || 1,
      dozenAmount: product.dozen_amount || 0,
      totalItems: totalItems,
      remainingItems: remainingItems,
      purchasePrice: parseFloat(product.purchasePrice) || 0,
      sellingPricePerItem: actualSellingPrice / itemsSold,
      sellingPrice: actualSellingPrice,
      originalSellingPrice: parseFloat(product.sellingPrice) || 0,
      purchaseDate: product.purchaseDate || new Date().toISOString(),
      noOfUnSold: quantity,
      status: "ACTIVE",
      shopId: product.shopId,
      shopName: product.shopName || "N/A",
    };

    setSaleItems([...saleItems, newItem]);

    setProductQuantities((prev) => ({ ...prev, [product.stockId]: "" }));
    setCustomSellingPrices((prev) => ({ ...prev, [product.stockId]: "" }));

    setError("");
    setTableError("");
    setErrorProductId(null);

    toastSuccess(`${product.productName} added to sale!`);
  };

  const removeSaleItem = (id) => {
    if (!id) return;
    setSaleItems(saleItems.filter((item) => item?.id !== id));
    setError("");
    setTableError("");
    setErrorProductId(null);
  };

  const handleSubmitAll = async () => {
    if (isSeller && !autoSeller) {
      const msg =
        "Your seller profile is not linked to this shop. Please contact your admin.";
      setError(msg);
      toastError(msg);
      return;
    }

    if (saleItems.length === 0) {
      setError("Please add at least one product to sell.");
      toastError("Please add at least one product to sell.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onAdd(saleItems);
    } catch (submitError) {
      const message =
        submitError?.message || "Could not create the sale. Please try again.";
      setError(message);
      toastError(message);
      return;
    } finally {
      setIsSubmitting(false);
    }

    setSaleItems([]);
    setProductQuantities({});
    setCustomSellingPrices({});
    setFilteredProducts([]);
    setFilteredCategories([]);
    setForm({ sellerId: "", productTypeId: "", productCategoryId: "" });
    setError("");
    setTableError("");
    setErrorProductId(null);
    setProductSearchTerm("");
    setExpandedProductKey(null);

    toastSuccess(`${saleItems.length} sale(s) added successfully!`);
    onClose();
  };

  const totalRevenue = saleItems.reduce((sum, item) => {
    if (!item) return sum;
    return sum + (item.sellingPrice || 0);
  }, 0);

  const totalProfit = saleItems.reduce((sum, item) => {
    if (!item) return sum;
    const costPerItem = item.purchasePrice / item.totalItems;
    const itemsSold = item.totalItems - item.noOfUnSold;
    return sum + ((item.sellingPricePerItem || 0) - costPerItem) * itemsSold;
  }, 0);

  const groupKeyFor = (stock) =>
    `${stock.productName}::${stock.productCategoryName}`;

  const productGroups = useMemo(() => {
    const map = new Map();

    (filteredProducts || []).forEach((stock) => {
      const key = groupKeyFor(stock);
      if (!map.has(key)) {
        map.set(key, {
          key,
          productName: stock.productName,
          productCategoryName: stock.productCategoryName,
          packageType: stock.packageType,
          stocks: [],
        });
      }
      const group = map.get(key);
      group.stocks.push(stock);
      group.packageType = group.packageType || stock.packageType;
    });

    return Array.from(map.values()).map((group) => {
      const totalItems = group.stocks.reduce(
        (sum, s) => sum + Number(s.totalItems || 0),
        0,
      );
      const remainingItems = group.stocks.reduce(
        (sum, s) => sum + Number(s.remainingItems || 0),
        0,
      );

      const availableStocks = group.stocks.filter((stock) => {
        const alreadyAdded = saleItems.some(
          (item) => item?.stockId === stock.stockId,
        );
        return !alreadyAdded && Number(stock.remainingItems) > 0;
      });

      return {
        ...group,
        totalItems,
        remainingItems,
        availableStocks,
        hasAvailableStocks: availableStocks.length > 0,
      };
    });
  }, [filteredProducts, saleItems]);

  const filteredProductGroupsBySearch = useMemo(() => {
    if (!productSearchTerm.trim()) return productGroups || [];
    const search = productSearchTerm.toLowerCase().trim();
    return (productGroups || []).filter((group) =>
      group?.productName?.toLowerCase().includes(search),
    );
  }, [productGroups, productSearchTerm]);

  if (isSeller && !autoSeller) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-center p-8">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="h-8 w-8 text-red-600"
          />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          Seller Profile Not Linked
        </h3>
        <p className="text-sm text-gray-600 max-w-md">
          Your seller profile is not linked to this shop, or it is currently
          inactive. Please contact your shop admin to resolve this.
        </p>
        <Button
          onClick={onClose}
          className="mt-2 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Close
        </Button>
      </div>
    );
  }

  if (enrichedStockData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-center p-8">
        <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="h-8 w-8 text-yellow-600"
          />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          No Products Available
        </h3>
        <p className="text-sm text-gray-600 max-w-md">
          There are no active products with remaining stock across your shops.
          Please add stock or activate products before creating a sale.
        </p>
        <Button
          onClick={onClose}
          className="mt-2 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faShoppingCart}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Add new sale
            </h3>
            <p className="text-sm text-gray-600">
              {isSeller
                ? "Choose products and add quantities"
                : "Select seller, choose products, and add quantities"}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="text-gray-600 hover:text-gray-800"
        >
          <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="h-4 w-4 text-red-500"
          />
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
          </button>
        </div>
      )}

      {isSeller ? (
        autoSeller && (
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex flex-col gap-3">
              <Label className="text-gray-700 font-medium flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faUserTie}
                  className="text-blue-800 h-3.5 w-3.5"
                />
                Seller
              </Label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span>
                  <strong>Name:</strong> {autoSeller._sellerName}
                </span>
                <span>
                  <strong>Reg No:</strong> {autoSeller._sellerRegNo}
                </span>
                <span>
                  <strong>Phone:</strong> {autoSeller._sellerPhone}
                </span>
                <span>
                  <strong>Email:</strong> {autoSeller._sellerEmail}
                </span>
              </div>
            </div>
          </Card>
        )
      ) : (
        <Card className="p-4 bg-white border border-gray-200">
          <div className="flex flex-col gap-3">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faUserTie}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Select Seller<span className="text-red-700">*</span>
            </Label>
            <Select
              value={form.sellerId}
              onValueChange={(value) => {
                setForm({ ...form, sellerId: value });
                setError("");
                setTableError("");
                setErrorProductId(null);
              }}
            >
              <SelectTrigger className="h-10 text-sm border-gray-300">
                <SelectValue placeholder="Choose a seller" />
              </SelectTrigger>
              <SelectContent>
                {activeSellers.length > 0 ? (
                  activeSellers.map((seller) => (
                    <SelectItem key={seller._sellerId} value={seller._sellerId}>
                      {seller._sellerName}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-600 text-center">
                    No active sellers available
                  </div>
                )}
              </SelectContent>
            </Select>
            {selectedSeller && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                <span>
                  <strong>Phone:</strong> {selectedSeller._sellerPhone}
                </span>
                <span>
                  <strong>Email:</strong> {selectedSeller._sellerEmail}
                </span>
                <span>
                  <strong>Reg No:</strong> {selectedSeller._sellerRegNo}
                </span>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card className="p-4 bg-white border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faBoxes}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Product Type<span className="text-red-700">*</span>
            </Label>
            <Select value={form.productTypeId} onValueChange={handleTypeChange}>
              <SelectTrigger className="h-10 text-sm border-gray-300">
                <SelectValue placeholder="Select product type" />
              </SelectTrigger>
              <SelectContent>
                {availableProductTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faBox}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Product Category<span className="text-red-700">*</span>
            </Label>
            <Select
              value={form.productCategoryId}
              onValueChange={handleCategoryChange}
              disabled={!form.productTypeId}
            >
              <SelectTrigger className="h-10 text-sm border-gray-300">
                <SelectValue placeholder="Select product category" />
              </SelectTrigger>
              <SelectContent>
                {filteredCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {filteredProductGroupsBySearch.length > 0 && (
        <Card className="p-4 bg-white border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <h4 className="text-sm font-semibold text-gray-700">
              Products ({filteredProductGroupsBySearch.length} available)
            </h4>
            <div className="w-full sm:w-64">
              <SearchBar
                value={productSearchTerm}
                onChange={setProductSearchTerm}
                placeholder="Search products by name..."
              />
            </div>
          </div>

          {tableError && (
            <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-4 w-4 text-red-500"
              />
              <span>{tableError}</span>
              <button
                onClick={() => {
                  setTableError("");
                  setErrorProductId(null);
                }}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                    Product
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                    Package
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                    Total Items
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                    Remaining
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                    Stocks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProductGroupsBySearch.map((group) => {
                  const isExpanded = expandedProductKey === group.key;

                  return (
                    <>
                      <tr
                        key={group.key}
                        onClick={() =>
                          setExpandedProductKey((prev) =>
                            prev === group.key ? null : group.key,
                          )
                        }
                        className={`cursor-pointer transition-colors ${
                          isExpanded ? "bg-blue-50/70" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="py-2 px-3">
                          <div>
                            <p className="font-medium text-blue-900">
                              {group.productName}
                            </p>
                            <p className="text-orange-600 font-semibold italic">
                              {group.productCategoryName || "N/A"}
                            </p>
                            <div className="md:hidden mt-1">
                              <p className="text-gray-800 text-xs">
                                Package: {group.packageType || "N/A"}
                              </p>
                              <p className="text-gray-600 font-medium text-xs">
                                Total: {group.totalItems} | Remaining:{" "}
                                {group.remainingItems}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                          {group.packageType || "N/A"}
                        </td>
                        <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                          {group.totalItems}
                        </td>
                        <td className="py-2 px-3 text-center hidden md:table-cell">
                          <span className="font-semibold text-green-600">
                            {group.remainingItems}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span
                              className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                                group.hasAvailableStocks
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {group.availableStocks.length}/
                              {group.stocks.length} available
                            </span>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className={`h-3.5 w-3.5 transition-all ${
                                isExpanded
                                  ? "text-blue-700 rotate-90"
                                  : "text-gray-500"
                              }`}
                            />
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr key={`${group.key}-expansion`}>
                          <td colSpan={5} className="p-0">
                            <div className="bg-blue-50/40 border-t border-b border-blue-100 px-3 py-2">
                              {group.availableStocks.length === 0 ? (
                                <p className="text-xs text-gray-600 text-center py-2">
                                  No available stocks for this product. All
                                  stocks either sold out or already added.
                                </p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-blue-200">
                                        <th className="text-left py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Stock
                                        </th>
                                        <th className="text-center py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Shop
                                        </th>
                                        <th className="text-center py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Total
                                        </th>
                                        <th className="text-center py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Remaining
                                        </th>
                                        <th className="text-center py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Selling price
                                        </th>
                                        <th className="text-center py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Unsold
                                        </th>
                                        <th className="text-center py-1.5 px-3 text-[11px] font-semibold text-gray-600 uppercase">
                                          Action
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.stocks.map((stock, stockIdx) => {
                                        const isAlreadyAdded = saleItems.some(
                                          (item) =>
                                            item?.stockId === stock.stockId,
                                        );
                                        const isErrorRow =
                                          errorProductId === stock.stockId;
                                        const remainingItems = Number(
                                          stock.remainingItems ?? 0,
                                        );

                                        return (
                                          <tr
                                            key={stock.stockId}
                                            className={`border-b border-blue-100 last:border-b-0 ${
                                              isErrorRow
                                                ? "bg-red-50"
                                                : isAlreadyAdded
                                                  ? "bg-gray-50 opacity-60"
                                                  : ""
                                            }`}
                                          >
                                            <td className="py-2 px-3">
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-900 text-white text-[11px] font-semibold">
                                                Stock #{stockIdx + 1}
                                              </span>
                                            </td>
                                            <td className="py-2 px-3 text-center text-gray-700">
                                              {stock.shopName || "N/A"}
                                            </td>
                                            <td className="py-2 px-3 text-center text-gray-700">
                                              {stock.totalItems}
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                              <span className="font-semibold text-green-600">
                                                {remainingItems}
                                              </span>
                                            </td>
                                            <td className="py-2 px-3">
                                              <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                disabled={isAlreadyAdded}
                                                value={
                                                  customSellingPrices[
                                                    stock.stockId
                                                  ] || ""
                                                }
                                                onChange={(e) =>
                                                  handleSellingPriceChange(
                                                    stock.stockId,
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder="Total revenue"
                                                className={`w-28 mx-auto text-center text-sm h-8 ${
                                                  isErrorRow
                                                    ? "border-red-500 ring-1 ring-red-500"
                                                    : ""
                                                }`}
                                              />
                                            </td>
                                            <td className="py-2 px-3">
                                              <Input
                                                type="number"
                                                min="0"
                                                max={remainingItems}
                                                disabled={isAlreadyAdded}
                                                value={
                                                  productQuantities[
                                                    stock.stockId
                                                  ] || ""
                                                }
                                                onChange={(e) =>
                                                  handleQuantityChange(
                                                    stock.stockId,
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder="0"
                                                className={`w-20 mx-auto text-center text-sm h-8 ${
                                                  isErrorRow
                                                    ? "border-red-500 ring-1 ring-red-500"
                                                    : ""
                                                }`}
                                              />
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                              {isAlreadyAdded ? (
                                                <span className="text-[11px] text-gray-500 italic">
                                                  Already added
                                                </span>
                                              ) : (
                                                <Button
                                                  size="sm"
                                                  className={`h-8 px-3 text-xs text-white bg-blue-900 hover:bg-blue-800 ${
                                                    isErrorRow
                                                      ? "border-red-500"
                                                      : ""
                                                  }`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    addSaleItem(stock);
                                                  }}
                                                >
                                                  <FontAwesomeIcon
                                                    icon={faPlus}
                                                    className="h-3 w-3 mr-1"
                                                  />
                                                  Add
                                                </Button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredProductGroupsBySearch.length === 0 && productSearchTerm && (
            <div className="text-center py-4 text-gray-600 text-sm">
              No products found matching "{productSearchTerm}"
            </div>
          )}
        </Card>
      )}

      {saleItems.length > 0 && (
        <Card className="p-4 bg-white border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <h4 className="text-sm font-semibold text-gray-700">
              Sale Items ({saleItems.length})
            </h4>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-gray-600">
                Total Revenue:{" "}
                <strong className="text-green-600">
                  {formatCurrency(totalRevenue)}
                </strong>
              </span>
              <span className="text-gray-600">
                Total Profit:{" "}
                <strong
                  className={`${
                    totalProfit >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {formatCurrency(totalProfit)}
                </strong>
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                    Product
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                    Seller
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                    Selling price
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                    Unsold
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 hidden sm:table-cell">
                    Revenue
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {saleItems.map((item) => {
                  if (!item) return null;
                  const revenue = item.sellingPrice || 0;

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-2 px-3">
                        <p className="font-medium text-gray-800">
                          {item.productName || "Unknown"}
                        </p>
                        <p className="text-xs text-gray-600 font-medium">
                          {item.productCategory || "N/A"}
                        </p>
                        <p className="text-xs text-gray-500">
                          Remaining: {item.remainingItems}
                        </p>
                      </td>
                      <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                        {item.sellerName || "Unknown"}
                      </td>
                      <td className="py-2 px-3 text-center font-medium text-gray-700">
                        {formatCurrency(item.sellingPrice)}
                      </td>
                      <td className="py-2 px-3 text-center font-semibold text-gray-700">
                        {item.noOfUnSold}
                      </td>
                      <td className="py-2 px-3 text-right text-green-600 font-medium hidden sm:table-cell">
                        {formatCurrency(revenue)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => removeSaleItem(item.id)}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="h-3.5 w-3.5"
                          />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={onClose}
          className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmitAll}
          disabled={
            saleItems.length === 0 || isSubmitting || (isSeller && !autoSeller)
          }
          className="gap-2 w-full sm:w-auto bg-blue-900 hover:bg-blue-800 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faSave} />
          {isSubmitting ? "Saving..." : `Save All Sales (${saleItems.length})`}
        </Button>
      </div>
    </div>
  );
};

export default AddSale;
